import { FEATURE_PRESETS, type FeaturePresetId } from "@baros/core";
import { FeaturesPanel } from "./FeaturesPanel.js";
import { useCallback, useEffect, useState } from "react";
import { money, toCents } from "@baros/core";
import { actingOrg, setActingOrg } from "../acting.js";
import { useApp } from "../context.js";
import { HOME, locationToUrl } from "../routes.js";
import type { ScreenProps } from "../screens.js";
import { platformApi, type NewOrg, type PlatformOrg } from "./api.js";
import "./platform.css";

/** Cents in the API, dollars in the fields. */
const dollars = (cents: number) => (cents / 100).toFixed(2);

/** A shelf's size, in the unit a person would say it in. */
function size(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
}

/** `1.5` → `1.50`, the multiplier as the field holds it. */
const times = (multiplier: number) => multiplier.toFixed(2);

/** `2026-09` → `Sep 2026`, for the history table. */
function monthLabel(period: string): string {
  const [year, month] = period.split("-").map(Number);
  const at = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1));
  return at.toLocaleDateString("en-AU", { month: "short", year: "numeric" });
}

/** The org's spend history as a CSV, built client-side — nothing leaves the browser. */
function historyCsv(o: PlatformOrg): string {
  const rows = [
    ["period", "chargedCents", "awsCostCents", "storageChargedCents", "turns", "tokens"],
    ...o.history.map((h) => [
      h.period,
      String(h.costCents),
      String(h.baseCents),
      String(h.storageCents),
      String(h.turns),
      String(h.tokens),
    ]),
  ];
  return rows.map((r) => r.join(",")).join("\n");
}

function downloadHistoryCsv(o: PlatformOrg): void {
  const blob = new Blob([historyCsv(o)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${o.orgId}-ai-spend-history.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

interface NewDraft {
  readonly name: string;
  readonly ownerEmail: string;
  readonly ownerName: string;
  readonly ceiling: string;
  readonly venueName: string;
  readonly suburb: string;
  readonly state: string;
  /** Chosen on purpose: what a new organisation starts with (D14). */
  readonly preset: FeaturePresetId | null;
}

const BLANK: NewDraft = {
  name: "",
  ownerEmail: "",
  ownerName: "",
  ceiling: "50.00",
  venueName: "",
  suburb: "",
  state: "VIC",
  preset: null,
};

/**
 * Screen 00 — the platform. Venox operators only: the registry hides it from
 * everyone else and every route behind it re-checks the operator flag, so
 * hiding the entry is a courtesy rather than the control.
 *
 * Opening an organisation sets the acting org for this tab and reloads; from
 * then on every request carries `x-baros-org` and the whole app is that
 * customer's, with the operator as its owner. "Leave" in the top bar undoes it.
 */
export function PlatformScreen(_p: ScreenProps) {
  const app = useApp();
  const [orgs, setOrgs] = useState<PlatformOrg[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<NewDraft>(BLANK);
  /** Per-org ceiling edits, dollars, until saved. */
  const [ceilings, setCeilings] = useState<Record<string, string>>({});
  /** Per-org price multiplier edits until saved. */
  const [multipliers, setMultipliers] = useState<Record<string, string>>({});
  /** Which orgs have their six-month history open. */
  const [historyOpen, setHistoryOpen] = useState<Record<string, boolean>>({});
  /** Which orgs have their features open (D14). */
  const [featuresOpen, setFeaturesOpen] = useState<Record<string, boolean>>({});
  const acting = actingOrg();

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await platformApi.list(app.token);
      setOrgs(r.orgs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not list the organisations");
    }
  }, [app.token]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (key: string, action: () => Promise<string | null>) => {
    setBusy(key);
    setError(null);
    setMsg(null);
    try {
      const note = await action();
      await load();
      if (note) setMsg(note);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not work");
    } finally {
      setBusy(null);
    }
  };

  const open = (orgId: string) => {
    setActingOrg(orgId);
    // Open it at home: the platform screen is not one of the customer's.
    window.location.assign(locationToUrl({ orgId, screen: HOME }));
  };

  const create = () =>
    run("create", async () => {
      const body: NewOrg = {
        name: draft.name.trim(),
        ownerEmail: draft.ownerEmail.trim(),
        ownerName: draft.ownerName.trim(),
        aiPlanCeilingCents: toCents(Number(draft.ceiling) || 0),
        venue: draft.venueName.trim()
          ? { name: draft.venueName.trim(), suburb: draft.suburb.trim(), state: draft.state.trim() }
          : null,
        preset: draft.preset ?? "full",
      };
      const r = await platformApi.create(app.token, body);
      setCreating(false);
      setDraft(BLANK);
      return r.owner.invited
        ? `${r.org.name} is set up. Cognito has emailed ${r.owner.email} a temporary password.`
        : `${r.org.name} is set up. ${r.owner.email} already had an account and now owns it.`;
    });

  const patch = (o: PlatformOrg, body: Parameters<typeof platformApi.patch>[2], note: string) =>
    run(o.orgId, async () => {
      await platformApi.patch(app.token, o.orgId, body);
      return note;
    });

  const totalSpend = (orgs ?? []).reduce((t, o) => t + o.usage.costCents, 0);
  const totalBase = (orgs ?? []).reduce((t, o) => t + o.usage.baseCents, 0);
  const suspended = (orgs ?? []).filter((o) => o.status === "suspended").length;

  return (
    <div className="screen">
      <div className="row screen-head" style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div className="label">
            {orgs ? orgs.length : "…"} {orgs?.length === 1 ? "organisation" : "organisations"}
            {suspended ? ` · ${suspended} suspended` : ""}
          </div>
          <div className="serif" style={{ fontSize: 28, lineHeight: 1.1, marginTop: 4 }}>Venox</div>
        </div>
        <span className="spacer" />
        <button className="btn dashed" style={{ height: 48 }} aria-pressed={creating} onClick={() => setCreating((v) => !v)}>
          + New organisation
        </button>
      </div>

      {error && (
        <div style={{ padding: "0 20px 10px" }}>
          <div className="msg err">{error}</div>
        </div>
      )}
      {msg && (
        <div style={{ padding: "0 20px 10px" }}>
          <div className="msg ok">{msg}</div>
        </div>
      )}

      <div className="scroll" style={{ padding: "0 20px 20px" }}>
        {creating && (
          <div className="pf-card">
            <div className="label">New organisation</div>
            <div className="pf-grid">
              <label className="field">
                <span>Organisation name</span>
                <input type="text" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="The Everleigh Group" />
              </label>
              <label className="field">
                <span>AI plan ceiling ($ / month)</span>
                <input className="num-input" style={{ width: "100%" }} type="number" min={0} step={5} value={draft.ceiling} onChange={(e) => setDraft({ ...draft, ceiling: e.target.value })} />
              </label>
              <label className="field">
                <span>Owner email</span>
                <input type="email" value={draft.ownerEmail} onChange={(e) => setDraft({ ...draft, ownerEmail: e.target.value })} placeholder="owner@venue.com.au" />
              </label>
              <label className="field">
                <span>Owner name</span>
                <input type="text" value={draft.ownerName} onChange={(e) => setDraft({ ...draft, ownerName: e.target.value })} placeholder="As it should appear" />
              </label>
            </div>
            <div className="label" style={{ marginTop: 4 }}>First venue · optional · the owner is made its manager</div>
            <div className="pf-grid three">
              <label className="field">
                <span>Venue name</span>
                <input type="text" value={draft.venueName} onChange={(e) => setDraft({ ...draft, venueName: e.target.value })} />
              </label>
              <label className="field">
                <span>Suburb</span>
                <input type="text" value={draft.suburb} onChange={(e) => setDraft({ ...draft, suburb: e.target.value })} />
              </label>
              <label className="field">
                <span>State</span>
                <input type="text" value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })} />
              </label>
            </div>
            <div className="label" style={{ marginTop: 4 }}>Starts with · every feature can be changed afterwards</div>
            <div className="pf-presets">
              {FEATURE_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="pf-preset"
                  aria-pressed={draft.preset === p.id}
                  onClick={() => setDraft({ ...draft, preset: p.id })}
                >
                  <span className="n">{p.label}</span>
                  <span className="s">{p.description}</span>
                </button>
              ))}
            </div>
            <div className="row" style={{ gap: 10 }}>
              <button className="btn primary" disabled={busy === "create" || !draft.name.trim() || !draft.ownerEmail.trim() || !draft.preset} onClick={() => void create()}>
                {busy === "create" ? "Creating…" : "Create and invite the owner"}
              </button>
              <button className="btn" onClick={() => setCreating(false)}>Cancel</button>
              <span className="spacer" />
              <span className="label" style={{ letterSpacing: "0.08em" }}>Cognito emails the owner a temporary password</span>
            </div>
          </div>
        )}

        <div className="table">
          <div className="thead">
            <span className="pf-c-name">Organisation</span>
            <span className="pf-c-size">Venues · people</span>
            <span className="pf-c-ai">AI this month</span>
          </div>
          {orgs === null && <div className="pad label">Reading the registry…</div>}
          {orgs?.map((o) => {
            const here = acting === o.orgId || (!acting && app.me.org.orgId === o.orgId);
            const editing = ceilings[o.orgId];
            const editingMult = multipliers[o.orgId];
            const multOk =
              editingMult !== undefined && Number(editingMult) >= 1 && Number(editingMult) <= 10;
            const showHistory = historyOpen[o.orgId] ?? false;
            const showFeatures = featuresOpen[o.orgId] ?? false;
            return (
              <div key={o.orgId}>
                <div className="trow pf-row" aria-current={here}>
                <div className="pf-main">
                <span className="pf-c-name">
                  <span className="n">{o.name}</span>
                  <span className="s">
                    <span className={o.status === "suspended" ? "neg" : "pos"}>{o.status}</span>
                    {" · "}
                    {o.orgId}
                    {o.status === "suspended" && o.suspendReason ? ` · ${o.suspendReason}` : ""}
                  </span>
                </span>
                <span className="pf-c-size num">
                  {o.venues} · {o.members}
                </span>
                <span className="pf-c-ai num">
                  {money(o.usage.costCents / 100)}
                  <span className="muted"> of {money(o.aiMonthlyLimitCents / 100)}</span>
                  <span className="pf-sub">{o.usage.turns} turns · {o.usage.period}</span>
                  <span className="pf-sub">
                    AWS {money(o.usage.baseCents / 100)} · shelf {size(o.storage.bytes)} {money(o.storage.chargedCents / 100)}
                  </span>
                </span>
                </div>
                {/* The plan's figures and the actions, on a line of their own: the
                    row never has to fit six columns across, whatever the width. */}
                <div className="pf-controls">
                <span className="pf-c-ceiling">
                  <span className="label">Plan ceiling $</span>
                  <input
                    className="num-input"
                    type="number"
                    min={0}
                    step={5}
                    aria-label={`AI plan ceiling for ${o.name}, dollars`}
                    value={editing ?? dollars(o.aiPlanCeilingCents)}
                    onChange={(e) => setCeilings({ ...ceilings, [o.orgId]: e.target.value })}
                  />
                  {editing !== undefined && editing !== dollars(o.aiPlanCeilingCents) && (
                    <button
                      className="btn sm"
                      disabled={busy === o.orgId}
                      onClick={() =>
                        void patch(o, { aiPlanCeilingCents: toCents(Number(editing) || 0) }, `${o.name}'s plan ceiling is now $${Number(editing).toFixed(2)} a month`).then(() =>
                          setCeilings(({ [o.orgId]: _dropped, ...rest }) => rest),
                        )
                      }
                    >
                      Save
                    </button>
                  )}
                </span>
                <span className="pf-c-mult">
                  <span className="label">Price ×</span>
                  <input
                    className="num-input"
                    type="number"
                    min={1}
                    max={10}
                    step={0.05}
                    aria-label={`AI price multiplier for ${o.name}`}
                    value={editingMult ?? times(o.aiPriceMultiplier)}
                    onChange={(e) => setMultipliers({ ...multipliers, [o.orgId]: e.target.value })}
                  />
                  {editingMult !== undefined && editingMult !== times(o.aiPriceMultiplier) && (
                    <button
                      className="btn sm"
                      disabled={busy === o.orgId || !multOk}
                      title={multOk ? undefined : "Between 1 and 10"}
                      onClick={() =>
                        void patch(o, { aiPriceMultiplier: Number(editingMult) }, `${o.name} is now charged ${Number(editingMult).toFixed(2)}× AWS cost for AI`).then(() =>
                          setMultipliers(({ [o.orgId]: _dropped, ...rest }) => rest),
                        )
                      }
                    >
                      Save
                    </button>
                  )}
                </span>
                <span className="pf-c-act">
                  <button
                    className="btn sm"
                    aria-pressed={showHistory}
                    onClick={() => setHistoryOpen({ ...historyOpen, [o.orgId]: !showHistory })}
                  >
                    History
                  </button>
                  <button
                    className="btn sm"
                    aria-pressed={showFeatures}
                    onClick={() => setFeaturesOpen({ ...featuresOpen, [o.orgId]: !showFeatures })}
                  >
                    Features
                  </button>
                  {here ? (
                    <span className="label" style={{ letterSpacing: "0.08em" }}>Acting here</span>
                  ) : (
                    <button className="btn sm" onClick={() => open(o.orgId)}>Open</button>
                  )}
                  {o.status === "suspended" ? (
                    <>
                      <button className="btn sm" disabled={busy === o.orgId} onClick={() => void patch(o, { status: "active" }, `${o.name} is reinstated`)}>
                        Reinstate
                      </button>
                      <button
                        className="btn sm danger"
                        disabled={busy === o.orgId || here}
                        title={here ? "Leave it first" : `Delete ${o.name} and everything in it`}
                        onClick={() => {
                          const typed = window.prompt(
                            `Delete ${o.name}? Every spec, count, order and person in it goes, and there is no undo. Type its id to confirm:\n${o.orgId}`,
                            "",
                          );
                          if (typed !== o.orgId) return;
                          void run(o.orgId, async () => {
                            const r = await platformApi.remove(app.token, o.orgId);
                            return `${o.name} is gone: ${r.deleted.items} items across ${r.deleted.venues} venue${r.deleted.venues === 1 ? "" : "s"}.`;
                          });
                        }}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn sm danger"
                      disabled={busy === o.orgId}
                      onClick={() => {
                        const reason = window.prompt(`Suspend ${o.name}? Every member gets "access suspended" on their next request. Reason (kept on the record):`, "");
                        if (reason === null) return;
                        void patch(o, { status: "suspended", reason }, `${o.name} is suspended`);
                      }}
                    >
                      Suspend
                    </button>
                  )}
                </span>
                </div>
              </div>
              {showFeatures && (
                <FeaturesPanel
                  org={o}
                  busy={busy === o.orgId}
                  onSave={(body, note) =>
                    run(o.orgId, async () => {
                      await platformApi.setFeatures(app.token, o.orgId, body);
                      return note;
                    })
                  }
                />
              )}
              {showHistory && (
                <div className="pf-history">
                  <div className="pf-history-head">
                    <span className="label">{o.name}, last six months</span>
                    <button className="btn sm" onClick={() => downloadHistoryCsv(o)}>
                      Download CSV
                    </button>
                  </div>
                  <div className="pf-history-rows">
                    {o.history.map((h) => (
                      <div className="pf-history-row" key={h.period}>
                        <span className="pf-h-period mono">{monthLabel(h.period)}</span>
                        <span className="pf-h-cost num mono">{money(h.costCents / 100)}</span>
                        <span className="pf-h-base num mono">AWS {money(h.baseCents / 100)}</span>
                        <span className="pf-h-base num mono">shelf {money(h.storageCents / 100)}</span>
                        <span className="pf-h-turns num mono">
                          {h.turns} turn{h.turns === 1 ? "" : "s"}
                        </span>
                        <span className="pf-h-tokens num mono">{h.tokens.toLocaleString("en-AU")} tok</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            );
          })}
          {orgs && orgs.length === 0 && <div className="pad label">No organisations yet.</div>}
        </div>

        {orgs && orgs.length > 0 && (
          <div className="label" style={{ paddingTop: 12, lineHeight: 1.7 }}>
            {money(totalSpend / 100)} of AI charged across the platform this month, for {money(totalBase / 100)} of
            AWS cost.
          </div>
        )}
      </div>
    </div>
  );
}
